import { getMimeType } from "../../models/file"

export interface ApiResponse<T> {
    status: number
    data: T
    ok: boolean
    error?: string
}

export async function fetchApi<T>(url: string, init: RequestInit): Promise<ApiResponse<T>> {
    const response = await fetch(url, init)
    const status = response.status

    if (!response.ok) {
        let error = response.statusText.trim()
        try {
            const errorData = await response.json()
            if (typeof errorData?.error === "string" && errorData.error.trim()) {
                error = errorData.error.trim()
            }
        } catch {
            // keep statusText as error
        }
        if (!error) {
            error = `Request failed with status ${status}`
        }
        return { status, data: undefined as T, ok: false, error }
    }

    if (status === 204) {
        return { status, data: undefined as T, ok: true }
    }

    // Read the body as text once and treat an empty body as `undefined`.
    // Do NOT rely on the `Content-Length` header: per the HTTP specs it is
    // optional (HTTP/2, RFC 9113 §8.1.1) or forbidden (HTTP/1.1 chunked,
    // RFC 9112 §6.2), so `headers.get("content-length")` returns `null` for a
    // spec-compliant empty 2xx body and a strict `=== "0"` guard cannot detect
    // it. Parsing the text (instead of `response.json()`) also avoids throwing
    // `SyntaxError: Unexpected end of JSON input` on those empty bodies.
    const text = await response.text()
    if (!text) {
        return { status, data: undefined as T, ok: true }
    }
    const data = JSON.parse(text)
    return { status, data, ok: true }
}

export function authHeaders(token: string, contentType?: string): HeadersInit {
    const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
    }
    if (contentType) {
        headers["Content-Type"] = contentType
    }
    return headers
}

export async function fileToBody(file: File): Promise<{ body: ArrayBuffer; contentType: string }> {
    const contentType = getMimeType(file.name)
    const body = await file.arrayBuffer()
    return { body, contentType }
}
